import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { ScrollArea } from '../ui/scroll-area';
import { Badge } from '../ui/badge';
import { Skeleton } from '../ui/skeleton';
import { Button } from '../ui/button';
import { Trophy, Star, Clock, MousePointer2, ExternalLink, Medal, Calendar, ArrowLeft, Users } from 'lucide-react';
import { formatTime } from '../../lib/gameUtils';
import { DIFFICULTIES } from '../../lib/constants';
import { cn, isMobileDevice } from '../../lib/utils';
import { logger } from '../../lib/utils';
import { PuzzleScoresModal } from './PuzzleScoresModal';
import axios from 'axios';

// Format date for display (e.g., "Dec 28" or "Dec 28, 2025" if different year)
const formatLeaderboardDate = (isoDate) => {
  if (!isoDate) return null;
  const date = new Date(isoDate);
  const now = new Date();
  const isCurrentYear = date.getFullYear() === now.getFullYear();
  
  const options = { month: 'short', day: 'numeric' };
  if (!isCurrentYear) {
    options.year = 'numeric';
  }
  return date.toLocaleDateString('en-US', options);
};

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function LeaderboardModal({ isOpen, onClose, onPlayImage }) {
  const [entries, setEntries] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedEntry, setSelectedEntry] = useState(null);
  
  useEffect(() => {
    if (isOpen) {
      fetchLeaderboard();
    }
  }, [isOpen]);
  
  const fetchLeaderboard = async () => {
    setIsLoading(true);
    setError(null);
    try {
      // Fetch from mobile or desktop leaderboard based on current device
      const response = await axios.get(`${API}/leaderboard?limit=40&is_mobile=${isMobileDevice()}`);
      setEntries(response.data.leaderboard || []);
    } catch (err) {
      logger.error('Failed to fetch leaderboard:', err);
      setError('Failed to load leaderboard');
    } finally {
      setIsLoading(false);
    }
  };
  
  const getRankIcon = (index) => {
    if (index === 0) return <Medal className="h-5 w-5 text-yellow-500" />;
    if (index === 1) return <Medal className="h-5 w-5 text-gray-400" />;
    if (index === 2) return <Medal className="h-5 w-5 text-amber-600" />;
    return <span className="w-5 text-center text-muted-foreground text-sm">{index + 1}</span>;
  };
  
  const getDifficultyBadge = (difficulty) => {
    const config = DIFFICULTIES[difficulty] || DIFFICULTIES.normal;
    return (
      <Badge 
        variant="outline" 
        className={cn(
          "text-xs px-1.5 py-0",
          difficulty === 'easy' && "bg-success/10 text-success border-success/30",
          difficulty === 'normal' && "bg-primary/10 text-primary border-primary/30",
          difficulty === 'hard' && "bg-destructive/10 text-destructive border-destructive/30"
        )}
      >
        {config.badge}
      </Badge>
    );
  };
  
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-2xl w-[calc(100vw-2rem)] max-w-[calc(100vw-2rem)] sm:w-auto max-h-[90vh] sm:max-h-[85vh] overflow-y-auto box-border" data-testid="leaderboard-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-heading">
            {/* Back button for mobile */}
            <Button 
              variant="ghost" 
              size="icon" 
              onClick={onClose}
              className="h-8 w-8 lg:hidden"
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <Trophy className="h-5 w-5 text-primary" />
            Leaderboard
          </DialogTitle>
        </DialogHeader>
        
        {isLoading ? (
          <div className="space-y-3">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="flex items-center gap-4 p-3">
                <Skeleton className="h-8 w-8 rounded-full" />
                <Skeleton className="h-4 flex-1" />
                <Skeleton className="h-4 w-16" />
              </div>
            ))}
          </div>
        ) : error ? (
          <div className="text-center py-8 text-muted-foreground">
            <p>{error}</p>
            <Button variant="outline" size="sm" className="mt-4" onClick={fetchLeaderboard}>
              Try Again
            </Button>
          </div>
        ) : entries.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <Trophy className="h-12 w-12 mx-auto mb-4 opacity-30" />
            <p>No scores yet. Be the first!</p>
          </div>
        ) : (
          <ScrollArea className="h-[500px] pr-4">
            <div className="space-y-2">
              {entries.map((entry, index) => (
                <div 
                  key={entry.id}
                  className={cn(
                    "flex items-center gap-3 p-3 rounded-lg border transition-colors",
                    index < 3 ? "bg-muted/50" : "bg-card hover:bg-muted/30"
                  )}
                >
                  {/* Rank */}
                  <div className="w-8 flex justify-center">
                    {getRankIcon(index)}
                  </div>
                  
                  {/* Player Info */}
                  <div 
                    className="flex-1 min-w-0 overflow-hidden cursor-pointer"
                    onClick={() => {
                      onPlayImage(entry.image_url);
                      onClose();
                    }}
                  >
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium truncate max-w-[100px] sm:max-w-none">{entry.player_name}</span>
                      {entry.perfect_game && (
                        <Star className="h-4 w-4 text-warning fill-warning flex-shrink-0" />
                      )}
                      {getDifficultyBadge(entry.difficulty)}
                    </div>
                    <div className="flex items-center gap-2 sm:gap-4 text-xs text-muted-foreground mt-1 flex-wrap">
                      <span className="flex items-center gap-1">
                        <Clock className="h-3 w-3 flex-shrink-0" />
                        {formatTime(entry.time_seconds)}
                      </span>
                      <span className="flex items-center gap-1">
                        <MousePointer2 className="h-3 w-3 flex-shrink-0" />
                        {entry.moves}
                      </span>
                      {entry.created_at && (
                        <span className="flex items-center gap-1">
                          <Calendar className="h-3 w-3 flex-shrink-0" />
                          {formatLeaderboardDate(entry.created_at)}
                        </span>
                      )}
                    </div>
                  </div>
                  
                  {/* Score */}
                  <div className="text-right flex-shrink-0">
                    <div className="font-heading font-bold text-base sm:text-lg">
                      {Math.round(entry.score)}
                    </div>
                    <div className="text-xs text-muted-foreground">pts</div>
                  </div>
                  
                  {/* View All Scores Button */}
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    className="h-8 w-8 flex-shrink-0"
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedEntry(entry);
                    }}
                    title="View all scores for this puzzle"
                  >
                    <Users className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
          </ScrollArea>
        )}
        
        {/* Puzzle Scores Modal */}
        <PuzzleScoresModal
          isOpen={!!selectedEntry}
          onClose={() => setSelectedEntry(null)}
          imageUrl={selectedEntry?.image_url}
          playerScore={null}
          title={`Scores for "${selectedEntry?.player_name}'s" Puzzle`}
        />
      </DialogContent>
    </Dialog>
  );
}
